import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import {
  ManufacturerItem,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
  PriceTextPlaceholder,
} from '../types/manufacturer-types';

interface ManufacturerStore {
  // Data state
  manufacturers: ManufacturerItem[];
  selectedManufacturer: ManufacturerItem | null;
  availablePlaceholders: PriceTextPlaceholder[];
  exampleTemplates: string[];
  finalCostExampleTemplates: string[];
  totalCount: number;

  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;

  // Error states
  error: string | null;  // For fetch/load errors (shown in grid)
  submitError: string | null;  // For create/update/delete errors (shown in blade)

  // Metadata
  lastUpdated: Date | null;

  // Actions
  fetchManufacturers: () => Promise<void>;
  fetchManufacturerById: (id: number) => Promise<void>;
  createManufacturer: (data: CreateManufacturerRequest) => Promise<ManufacturerItem>;
  updateManufacturer: (id: number, data: UpdateManufacturerRequest) => Promise<ManufacturerItem>;
  deleteManufacturer: (id: number) => Promise<void>;
  setSelectedManufacturer: (manufacturer: ManufacturerItem | null) => void;
  clearError: () => void;
  clearSubmitError: () => void;
  reset: () => void;
}

const initialState = {
  manufacturers: [],
  selectedManufacturer: null,
  availablePlaceholders: [],
  exampleTemplates: [],
  finalCostExampleTemplates: [],
  totalCount: 0,
  isLoading: false,
  isSubmitting: false,
  error: null,
  submitError: null,
  lastUpdated: null,
};

export const useManufacturerStore = create<ManufacturerStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchManufacturers: async () => {
        const state = get();
        if (state.isLoading) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const response = await services.getManufacturersUseCase.execute();

          set({
            manufacturers: response.value,
            totalCount: response['@odata.count'] ?? response.value.length,
            availablePlaceholders: response.availablePlaceholders ?? [],
            exampleTemplates: response.exampleTemplates ?? [],
            finalCostExampleTemplates: response.finalCostExampleTemplates ?? [],
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          console.error('ManufacturerStore.fetchManufacturers: Failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load manufacturers',
          });
        }
      },

      fetchManufacturerById: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const manufacturer = await services.getManufacturerByIdUseCase.execute(id);

          set({
            selectedManufacturer: manufacturer,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error(`ManufacturerStore.fetchManufacturerById(${id}): Failed:`, error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load manufacturer',
          });
        }
      },

      createManufacturer: async (data: CreateManufacturerRequest) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          const newManufacturer = await services.createManufacturerUseCase.execute(data);

          // Refresh the list after creation
          await get().fetchManufacturers();

          set({ isSubmitting: false, submitError: null });
          return newManufacturer;
        } catch (error) {
          console.error('ManufacturerStore.createManufacturer: Failed:', error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to create manufacturer',
          });
          throw error;
        }
      },

      updateManufacturer: async (id: number, data: UpdateManufacturerRequest) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          const updatedManufacturer = await services.updateManufacturerUseCase.execute(id, data);

          // Refresh the list after update
          await get().fetchManufacturers();

          set({
            isSubmitting: false,
            selectedManufacturer: updatedManufacturer,
            submitError: null,
          });
          return updatedManufacturer;
        } catch (error) {
          console.error(`ManufacturerStore.updateManufacturer(${id}): Failed:`, error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to update manufacturer',
          });
          throw error;
        }
      },

      deleteManufacturer: async (id: number) => {
        set({ isSubmitting: true, submitError: null });

        try {
          const services = servicesContainer.getServices();
          await services.deleteManufacturerUseCase.execute(id);

          // Refresh the list after deletion
          await get().fetchManufacturers();

          // Clear selected if it was the deleted one
          const state = get();
          if (state.selectedManufacturer?.id === id) {
            set({ selectedManufacturer: null });
          }

          set({ isSubmitting: false, submitError: null });
        } catch (error) {
          console.error(`ManufacturerStore.deleteManufacturer(${id}): Failed:`, error);
          set({
            isSubmitting: false,
            submitError: error instanceof Error ? error.message : 'Failed to delete manufacturer',
          });
          throw error;
        }
      },

      setSelectedManufacturer: (manufacturer: ManufacturerItem | null) => {
        set({ selectedManufacturer: manufacturer });
      },

      clearError: () => set({ error: null }),

      clearSubmitError: () => set({ submitError: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'manufacturer-storage',
      partialize: () => ({}), // Don't persist any state
    }
  )
);
